<template>
  <SACModal
    modalClass="setting-action-modal"
    :cardClass="`${actionModalView}-setting`"
    :show="showActionModal"
    :close="() => toggleActionModal(false)"
  >
    <template
      v-if="(actionModalView === 'edit-user' || actionModalView === 'new-user')"
      #content
    >
      <PlatformUserForm />
    </template>
    <template
      v-else-if="actionModalView === 'change-password'"
      #content
    >
      <ChangePassword />
    </template>
    <template
      v-else-if="actionModalView === 'plan-builder'"
      #content
    >
      <PlanBuilder
        :close="() => toggleActionModal(false)"
        :corporation="currentCorporation"
        :partnerSettings="partnerSettings"
      />
    </template>
    <template
      v-else-if="actionModalView === 'payment-methods'"
      #content
    >
      <AllPaymentMethods
        :corpId="currentCorporation.id"
        :selectedPaymentMethod="selectedPaymentMethod"
        :toggleActionModal="toggleActionModal"
        :changes="changeSettingValues"
      />
    </template>
    <template
      v-else-if="actionModalView === 'new-payment-method'"
      #content
    >
      <NewPaymentMethod
        :corpId="currentCorporation.id"
        :firstName="currentAdmin.firstName"
        :lastName="currentAdmin.lastName"
        :toEmail="currentAdmin.email"
        :changes="changeSettingValues"
        :toggleActionModal="toggleActionModal"
        :onSubmit="handleNewPaymentMethod"
        :showCreditCard="partnerSettings?.INVOICE_CREDIT_CARD_ENABLED"
      />
    </template>
    <template
      v-else-if="actionModalView === 'warning-payment'"
      #content
    >
      <DeletePaymentWarning
        :corpId="currentCorporation.id"
        :firstName="currentAdmin.firstName"
        :lastName="currentAdmin.lastName"
        :toEmail="currentAdmin.email"
        :partnerSettings="partnerSettings"
        :changes="changeSettingValues"
        :toggleActionModal="toggleActionModal"
        :onSubmit="handleNewPaymentMethod"
      />
    </template>
    <template
      v-else-if="actionModalView === 'warning-chargebee-contact'"
      #content
    >
      <DeleteEmailWarning
        :corpId="currentCorp.id"
        :firstName="currentAdmin.firstName"
        :lastName="currentAdmin.lastName"
        :toEmail="currentAdmin.email"
        :partnerSettings="partnerSettings"
        :changes="changeSettingValues"
        :toggleActionModal="toggleActionModal"
        :onSubmit="handleNewPaymentMethod"
      />
    </template>
    <template
      v-else-if="actionModalView === 'confirm-mandate'"
      #content
    >
      <ConfirmMandate
        :billingAddress="currentCorporation.billingAddress[0]"
        :setupIntent="setupIntent"
        :changes="changeSettingValues"
        :toggleActionModal="toggleActionModal"
        :onSubmit="handleNewPaymentMethod"
      />
    </template>
    <template
      v-else-if="actionModalView === 'verification-required'"
      #content
    >
      <VerificationRequired
        :toggleActionModal="toggleActionModal"
        :navigate="() => toggleActionModal(false)"
        body="Once you completed the Micro-Deposit verification your bank account will show up as payment method!"
      />
    </template>
    <template
      v-else-if="actionModalView === 'credit-card'"
      #content
    >
      <CreditCard
        :close="() => toggleActionModal(true, 'new-payment-method')"
        :onSubmit="handleCreditCard"
      />
    </template>
  </SACModal>
</template>

<script>
import SACModal from '@shared/components/sac/SACModal.vue';
import PlatformUserForm from './PlatformUserForm.vue';
import ChangePassword from './ChangePassword.vue';
import PlanBuilder from '../partnerSettings/providerPlan/PlanBuilder.vue';
import AllPaymentMethods from '../shared/billingModal/AllPaymentMethods.vue';
import NewPaymentMethod from '../shared/billingModal/NewPaymentMethod.vue';
import DeletePaymentWarning from '../shared/billingModal/DeletePaymentWarning.vue';
import DeleteEmailWarning from '../shared/billingModal/DeleteEmailWarning.vue';
import CreditCard from '../shared/billingModal/CreditCard.vue';
import ConfirmMandate from '../shared/billingModal/ConfirmMandate.vue';
import VerificationRequired from '../shared/billingModal/VerificationRequired.vue';

import settingMixin from '../../mixins/settingMixin';

export default {
  name: 'ActionModal',
  components: {
    SACModal,
    PlatformUserForm,
    ChangePassword,
    PlanBuilder,
    AllPaymentMethods,
    NewPaymentMethod,
    CreditCard,
    ConfirmMandate,
    VerificationRequired,
    DeletePaymentWarning,
    DeleteEmailWarning,
  },
  props: {
    currentCorp: Object,
  },
  mixins: [settingMixin],
  methods: {
    async handleCreditCard(tokenId) {
      this.changeSettingValues([{
        field: 'cardToken',
        value: tokenId,
      }, {
        field: 'paymentMethod',
        value: 'Credit Card',
      }]);
      await this.$store.dispatch('addBillingPaymentMethod');
      this.toggleActionModal(false);
    },
    async handleNewPaymentMethod() {
      await this.$store.dispatch('addBillingPaymentMethod');
      this.toggleActionModal(false);
    },
  },
};
</script>

<style lang="scss">

.setting-action-modal {
  .edit-user-setting, .new-user-setting {
    width: 650px;
    height: 600px;
    .ac-body-container {
      overflow-y: auto;
    }
  }
  .change-password-setting {
    width: 500px;
    height: 380px;
  }
  .new-payment-method-setting {
    width: 400px;
    height: 500px;
  }
  .payment-methods-setting {
    width: 400px;
    height: 500px;
  }
  .credit-card-setting {
    width: 400px;
    height: 280px;
  }
  .confirm-mandate-setting {
    width: 600px;
    height: 480px;
  }
  .verification-required-setting {
    width: 500px;
    height: 300px;
  }
  .ac-card-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    animation: fadeIn 0.5s ease;
    position: relative;
    .ac-header-container {
      padding: 29px 24px 19px 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid $NBLUE10;
      .ac-header {
        font-size: 1.25rem;
        font-weight: 500;
      }
      .ac-close-button {
        width: 32px;
        height: 32px;
        border-radius: 6px;
        background-color: $NBLUE10;
        display: flex;
        align-items: center;
        justify-content: center;
        &:hover {
          background-color: $NBLUE20;
        }
        .ac-close {
          width: 10px;
          height: 10px;
        }
      }
    }
    .ac-body-container {
      flex: 1;
      padding: 31px 24px 26px 24px;
     .error-message {
       color: $TOMATO100;
     }
      .ac-body-header {
        font-size: 1.25rem;
        font-weight: 500;
        text-align: center;
        margin-bottom: 25px;
      }
      .ac-body-sub-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;
      }
      .ac-body-tri-container {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 2rem;
      }
      .ac-input-container {
        margin-right: 0;
        .ac-input-header {
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: 3px;
          .ac-input-sub {
            font-size: 0.75rem;
            color: $N50;
          }
          .ac-input-text {
            font-size: 0.85rem;
            color: $N70;
          }
          .required {
            color: $TOMATO100;
          }
        }
        .match-background {
          background: $NBLUE5;
          border-radius: 3px;
          border: unset;
        }
        textarea {
          width: 100%;
          height: 110px;
          padding: 8px 10px;
          border-radius: 2px;
          background-color: $NBLUE5;
          border: none;
          resize: none;
        }
        .ac-input-error {
          color: $TOMATO100;
          font-size: 0.875rem;
        }
      }
      .permission-section {
        border-top:  2px solid $N5;
        margin-top: 1rem;
      }
    }
  }
  .ac-footer-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 16px 24px;
    border-top: 1px solid $NBLUE10;
    .ac-form-button {
      padding: 0 16px;
      height: 40px;
      border-radius: 6px;
      background-color: $NBLUE100;
      color: $WHITE;
      font-weight: 600;
      &:hover {
        background-color: $NBLUE90;
      }
    }
    .ac-form-deactivate {
      padding: 0 16px;
      height: 40px;
      border-radius: 6px;
      background-color: $TOMATO100;
      color: $WHITE;
      font-weight: 600;
      margin-right: 10px;
      &:hover {
        background-color: $TOMATO70;
      }
    }
    .ac-form-cancel-button {
      width: 120px;
      height: 40px;
      border-radius: 6px;
      background-color: $NBLUE10;
      font-weight: 600;
      &:hover {
        background-color: $NBLUE20;
      }
    }
  }
  .ac-overlay {
    border-radius: 12px;
  }
  .new-icn-btn {
    color: $NBLUE110;
    border-radius: 4px;
    padding: 10px 16px;
    border: 2px solid $NBLUE110;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
  }
  .new-fill-btn {
    margin-left: 12px;
    color: white;
    background-color: $NBLUE110;
    border-radius: 4px;
    padding: 10px 16px;
    border: 2px solid $NBLUE110;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
  }
  .new-del-btn {
    color: $WHITE;
    background-color: $TOMATO200;
    margin-left: 12px;
    border-radius: 4px;
    padding: 10px 16px;
    border: 2px solid $TOMATO200;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
  }
}
</style>
